// src/pages/EditCustomTemplate.js
import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import FormulaBuilderModal from '../components/FormulaBuilderModal'
import './CreateCustomTemplate.css'

const API = process.env.REACT_APP_API_URL || ''

const MANDATORY_FIELDS = [
  { key: 'productName',              label: 'Product Name',           type: 'text' },
  { key: 'periodExact',              label: 'Date',                   type: 'date' },
  { key: 'totalCostsKesPerKg',       label: 'Total Cost (Kes/Kg)',    type: 'computed', formula: '' },
  { key: 'sellingPriceKesPerKg',     label: 'Selling Price (Kes/Kg)', type: 'number' },
  { key: 'grossProfitMarginPercent', label: 'Gross Profit (%)',       type: 'computed', formula: '' }
]

export default function EditCustomTemplate() {
  const { id }      = useParams()
  const nav         = useNavigate()
  const fieldRefs   = useRef({})

  const isEdit = Boolean(id)
  const [name, setName]                           = useState('')
  const [currency, setCurrency]                   = useState('USD')
  const [fields, setFields]                       = useState(isEdit ? [] : [...MANDATORY_FIELDS])
  const [newField, setNewField]                   = useState({ key:'', label:'', type:'text', formula:'', options:'' })
  const [editingIndex, setEditingIndex]           = useState(null)
  const [editingFormulaKey, setEditingFormulaKey] = useState(null)
  const [showNewFormulaModal, setShowNewFormulaModal] = useState(false)
  const [optionFormulaIdx, setOptionFormulaIdx] = useState(null)
  const [applyToExisting, setApplyToExisting] = useState(true)

  function toCamelCase(str = '') {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .map((w, i) =>
        i === 0
          ? w.toLowerCase()
          : w[0].toUpperCase() + w.slice(1).toLowerCase()
      )
      .join('')
  }

  function cancel() {
    if (window.confirm('Discard changes?')) nav('/costcards/new')
  }

  // ─── Load saved template fields (order + formulas intact) ─────────────────
  useEffect(() => {
    if (!isEdit) return
    fetch(`${API}/api/custom-templates/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(tmpl => {
        setName(tmpl.name)
        setCurrency(tmpl.settings?.currency || 'USD')
        setFields(tmpl.fields || [])
      })
      .catch(() => {
        alert('Template not found')
        nav('/costcards/new')
      })
  }, [id, isEdit, nav])

  // ─── Label → key auto‐gen for new fields ─────────────────────────────────
  function handleLabelChange(e) {
    const label = e.target.value
    setNewField(n => ({
      ...n,
      label,
      // Only auto-generate key for brand new fields. When editing existing fields, keep the key stable.
      key: editingIndex === null ? toCamelCase(label) : n.key
    }))
  }

  // ─── Add / update an existing field ─────────────────────────────────────
  function startEditingField(i) {
    const f = fields[i]
    let options = f.options
    // If options is a string (CSV), convert to array
    if (typeof options === 'string' && options.trim()) {
      options = options.split(',').map(opt => ({ label: opt.trim(), value: opt.trim(), formula: '' }))
    }
    setEditingIndex(i)
    setNewField({
      key:     f.key,
      label:   f.label,
      type:    f.type,
      formula: f.formula || '',
      options: Array.isArray(options) ? options : []
    })
  }

  function addOrUpdateField() {
    // Merge newField with the old field (if exists) to preserve all properties
    let oldField = editingIndex !== null ? fields[editingIndex] : {};
    const f = {
      ...oldField,
      key: newField.key,
      label: newField.label,
      type: newField.type,
      formula: newField.type === 'computed' ? newField.formula : undefined,
      options: (newField.type === 'dropdown' || newField.type === 'select') ? (Array.isArray(newField.options) ? newField.options : []) : undefined
    };
    // Remove undefined keys
    Object.keys(f).forEach(k => f[k] === undefined && delete f[k]);
    const nxt = [...fields]
    if(editingIndex!==null) nxt[editingIndex] = f
    else nxt.push(f)
    setFields(nxt)
    // Reload the field by key from the latest fields array
    const updated = nxt.find(field => field.key === f.key);
    setEditingIndex(nxt.findIndex(field => field.key === f.key));
    setNewField({
      key: updated.key,
      label: updated.label,
      type: updated.type,
      formula: updated.formula || '',
      options: (updated.type === 'dropdown' || updated.type === 'select') ? (Array.isArray(updated.options) ? updated.options : []) : []
    })
  }

  // ─── Formula builder for existing fields ─────────────────────────────────
  function openFormulaEditor(key) {
    const el = fieldRefs.current[key]
    if (el?.scrollIntoView) el.scrollIntoView({ behavior:'smooth', block:'center' })
    setEditingFormulaKey(key)
  }
  function saveFormula(expr) {
    setFields(fields.map(f =>
      f.key === editingFormulaKey ? { ...f, formula: expr } : f
    ))
    setEditingFormulaKey(null)
  }

  // ─── Formula builder for new field ───────────────────────────────────────
  function openNewFieldFormula() { setShowNewFormulaModal(true) }
  function saveNewFieldFormula(expr) {
    setNewField(n => ({ ...n, formula: expr }))
    setShowNewFormulaModal(false)
  }

  // ─── Drag & drop handler ────────────────────────────────────────────────
  function onDragEnd(result) {
    if (!result.destination) return
    const nxt = Array.from(fields)
    const [mv] = nxt.splice(result.source.index, 1)
    nxt.splice(result.destination.index, 0, mv)
    setFields(nxt)
  }

  function addOptionRow() {
    setNewField(n => ({
      ...n,
      options: Array.isArray(n.options) ? [...n.options, { value: '', label: '', formula: '' }] : [{ value: '', label: '', formula: '' }]
    }))
  }
  function updateOptionLabel(idx, label) {
    const value = toCamelCase(label)
    setNewField(n => {
      const opts = Array.isArray(n.options) ? [...n.options] : []
      opts[idx] = { ...opts[idx], label, value }
      return { ...n, options: opts }
    })
  }
  function removeOptionRow(idx) {
    setNewField(n => {
      const opts = Array.isArray(n.options) ? n.options.filter((_, i) => i !== idx) : []
      return { ...n, options: opts }
    })
  }
  function openOptionFormula(idx) {
    setOptionFormulaIdx(idx)
  }
  function saveOptionFormula(expr) {
    setNewField(n => {
      const opts = Array.isArray(n.options) ? [...n.options] : []
      opts[optionFormulaIdx].formula = expr
      return { ...n, options: opts }
    })
    setOptionFormulaIdx(null)
  }

  // ─── Persist template back to server ────────────────────────────────────
  async function saveTemplate() {
    if (!name.trim()) {
      alert('Template name is required')
      return
    }
    const payload = {
      name,
      key: name.replace(/\s+/g,'').toLowerCase(),
      settings: {
        currency,
        currencyOptions: [
          { value:'USD', label:'USD' },
          { value:'EUR', label:'EUR' }
        ]
      },
      fields,
      applyToExisting
    }
    const res = await fetch(`${API}/api/custom-templates/${id}`, {
      method:'PUT',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload)
    })
    if (res.status === 409) {
      alert('A template with that name already exists.')
      return
    }
    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText)
      alert(txt)
      return
    }
    nav('/costcards/new')
  }

  return (
    <div className="create-custom-template">
      <h1>Edit Custom Template</h1>

      <div className="form-row">
        <label>Template Name:</label>
        <input value={name} onChange={e=>setName(e.target.value)} />
      </div>
      <div className="form-row">
        <label>Currency:</label>
        <select value={currency} onChange={e=>setCurrency(e.target.value)}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </div>
      <div className="form-row">
        <label>Apply renamed labels to existing cost cards</label>
        <input
          type="checkbox"
          checked={applyToExisting}
          onChange={e=>setApplyToExisting(e.target.checked)}
          style={{ marginLeft: 8 }}
        />
      </div>

      <hr/>
      <h2>Fields (drag to reorder)</h2>

      <DragDropContext onDragEnd={onDragEnd}>
        {/* **explicit** vertical direction to restore between‐items dropping */}
        <Droppable droppableId="fields-list" direction="vertical">
          {provided => (
            <ul
              className="fields-list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {fields.map((f,i) => (
                <Draggable key={f.key} draggableId={f.key} index={i}>
                  {(prov,snap) => (
                    <li
                      ref={node => {
                        prov.innerRef(node)
                        fieldRefs.current[f.key] = node
                      }}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        marginBottom: '4px',
                        background: snap.isDragging ? '#f0f8ff' : '#fff',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        ...prov.draggableProps.style
                      }}
                    >
                      <strong style={{ flexGrow:1 }}>{f.label}</strong>
                      {f.type==='computed' && (
                        <button onClick={()=>openFormulaEditor(f.key)} title="Edit formula">⚙️</button>
                      )}
                      {i >= MANDATORY_FIELDS.length && (
                        <>
                          <button onClick={()=>startEditingField(i)} title="Edit field">✏️</button>
                          <button onClick={()=>{
                              if(window.confirm('Remove this field?')) {
                                setFields(fs=>fs.filter((_,j)=>j!==i))
                                if(editingIndex===i) setEditingIndex(null)
                              }
                            }} title="Delete field">🗑️</button>
                        </>
                      )}
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {editingFormulaKey && (
        <FormulaBuilderModal
          field={editingFormulaKey}
          existingExpr={fields.find(f=>f.key===editingFormulaKey)?.formula||''}
          fieldsList={fields.map(f=>f.key)}
          labels={Object.fromEntries(fields.map(f=>[f.key,f.label]))}
          onSave={saveFormula}
          onCancel={()=>setEditingFormulaKey(null)}
        />
      )}

      <div className="add-field-box">
        <h3>{editingIndex!==null ? 'Update Field' : 'Add Field'}</h3>
        <div style={{ background: '#f8fafc', color: '#64748b', fontSize: 12, padding: 8, margin: '8px 0', border: '1px solid #e2e8f0', borderRadius: 4 }}>
          <strong>DEBUG newField:</strong>
          <pre style={{ margin: 0 }}>{JSON.stringify(newField, null, 2)}</pre>
        </div>
        <div className="add-field-row">
          <label>Field Label:</label>
          <input
            className="large-input"
            placeholder="e.g. Cost per Kg"
            value={newField.label}
            onChange={handleLabelChange}
          />
        </div>
        <div className="add-field-row">
          <label>Field ID:</label>
          <input
            className="small-input"
            value={newField.key}
            onChange={e => setNewField(n => ({
              ...n,
              // Preserve user-provided casing; strip spaces and non-alphanumerics except underscore
              key: e.target.value.replace(/[^A-Za-z0-9_]/g, '')
            }))}
            placeholder="e.g. costPerKgCurrency"
          />
        </div>
        <div className="add-field-row">
          <label>Type:</label>
          <select
            value={newField.type}
            onChange={e=>setNewField(n=>({...n,type:e.target.value}))}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="select">Dropdown</option>
            <option value="computed">Formula</option>
            <option value="date">Date</option>
          </select>
        </div>

        {/* Advanced Dropdown/Select Option UI */}
        {(newField.type === 'dropdown' || newField.type === 'select') && (
          <div className="dropdown-options-ui" style={{ marginTop: 12 }}>
            <table style={{ width: '100%', marginBottom: 8, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Label</th>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Formula</th>
                  <th style={{ borderBottom: '2px solid #e2e8f0' }}></th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(newField.options) && newField.options.map((opt, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '4px 8px' }}>
                      <input
                        value={opt.label}
                        onChange={e => updateOptionLabel(idx, e.target.value)}
                        placeholder="Option label"
                        style={{ width: '100%', padding: '4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <input
                        value={opt.value}
                        onChange={e => setNewField(n => {
                          const opts = [...n.options]
                          opts[idx] = { ...opts[idx], value: e.target.value }
                          return { ...n, options: opts }
                        })}
                        placeholder="Option ID"
                        style={{ width: '100%', padding: '4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px', minWidth: 120 }}>
                      <span style={{ fontFamily: 'monospace', color: opt.formula ? '#2563eb' : '#64748b' }}>
                        {opt.formula || <span style={{ color: '#bdbdbd' }}>—</span>}
                      </span>
                      <button
                        type="button"
                        title="Edit formula"
                        style={{ marginLeft: 8, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#2563eb' }}
                        onClick={() => openOptionFormula(idx)}
                      >
                        ⚙️
                      </button>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <button type="button" onClick={() => removeOptionRow(idx)} style={{ color: '#ef4444', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={addOptionRow}
              style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, fontSize: 15, float: 'right', marginBottom: 8, cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,0.08)' }}
            >
              + Add Option
            </button>
            {optionFormulaIdx !== null && (
              <FormulaBuilderModal
                field={newField.options[optionFormulaIdx]?.label || ''}
                existingExpr={newField.options[optionFormulaIdx]?.formula || ''}
                fieldsList={fields.map(f => f.key)}
                labels={Object.fromEntries(fields.map(f => [f.key, f.label]))}
                onSave={saveOptionFormula}
                onCancel={() => setOptionFormulaIdx(null)}
              />
            )}
          </div>
        )}

        {newField.type==='computed' && (
          <div className="add-field-row">
            <button className="btn-add-formula" onClick={openNewFieldFormula}>Build Formula…</button>
            {newField.formula && <div className="formula-preview"><code>{newField.formula}</code></div>}
          </div>
        )}

        <button className="btn-add" onClick={addOrUpdateField}>
          {editingIndex!==null ? 'Update Field' : 'Add Field'}
        </button>
      </div>

      {showNewFormulaModal && (
        <FormulaBuilderModal
          field={newField.key}
          existingExpr={newField.formula}
          fieldsList={fields.map(f=>f.key)}
          labels={Object.fromEntries(fields.map(f=>[f.key,f.label]))}
          onSave={saveNewFieldFormula}
          onCancel={()=>setShowNewFormulaModal(false)}
        />
      )}

      <hr/>
      <div className="form-actions">
        <button className="btn-cancel" onClick={cancel}>Cancel</button>
        <button className="btn-save" onClick={saveTemplate}>Update Template</button>
      </div>
    </div>
  )
}
